import { Editor } from '@tiptap/core';
import { Menu, MenuList, MenuPopover, MenuTrigger, ToggleButton, Tooltip } from '@fluentui/react-components';

import ToolbarMenuButton from '@modules/documents/components/header/toolbar/ToolbarMenuButton';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';
import { getHorizontalAlignmentState } from '@modules/documents/components/header/alignmentState';
import { ETiptapAlignmentSelectorKey } from '@modules/documents/types/tiptap.types';
import { ETableCellVerticalAlignment } from './cellAttributes';
import CellAlignmentIcon from './CellAlignmentIcon';
import { getVerticalAlignmentState, setCellAlignment } from './cellAlignmentCommands';

type TProps = {
  editor: Editor;
  showTooltip: boolean;
};

type TGridHorizontal =
  | ETiptapAlignmentSelectorKey.LEFT
  | ETiptapAlignmentSelectorKey.CENTER
  | ETiptapAlignmentSelectorKey.RIGHT;

type TOption = {
  vertical: ETableCellVerticalAlignment;
  horizontal: TGridHorizontal;
  label: string;
};

const { TOP, CENTER: MIDDLE, BOTTOM } = ETableCellVerticalAlignment;
const { LEFT, CENTER, RIGHT } = ETiptapAlignmentSelectorKey;

const OPTIONS: TOption[] = [
  { vertical: TOP, horizontal: LEFT, label: 'Align top left' },
  { vertical: TOP, horizontal: CENTER, label: 'Align top center' },
  { vertical: TOP, horizontal: RIGHT, label: 'Align top right' },
  { vertical: MIDDLE, horizontal: LEFT, label: 'Align middle left' },
  { vertical: MIDDLE, horizontal: CENTER, label: 'Align middle center' },
  { vertical: MIDDLE, horizontal: RIGHT, label: 'Align middle right' },
  { vertical: BOTTOM, horizontal: LEFT, label: 'Align bottom left' },
  { vertical: BOTTOM, horizontal: CENTER, label: 'Align bottom center' },
  { vertical: BOTTOM, horizontal: RIGHT, label: 'Align bottom right' },
];

const CellAlignmentMenuButton = ({ editor, showTooltip }: TProps) => {
  const vertical = useEditorActive(getVerticalAlignmentState);
  const horizontal = useEditorActive(getHorizontalAlignmentState);
  const active = OPTIONS.find((option) => option.vertical === vertical && option.horizontal === horizontal);

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <ToolbarMenuButton
          icon={<CellAlignmentIcon vertical={active?.vertical ?? TOP} horizontal={active?.horizontal ?? LEFT} />}
          tooltip={showTooltip ? 'Cell alignment' : undefined}
        ></ToolbarMenuButton>
      </MenuTrigger>
      <MenuPopover style={{ minWidth: 'auto' }}>
        <MenuList>
          <div className="grid w-fit grid-cols-3 gap-px">
            {OPTIONS.map((option) => (
              <Tooltip key={option.label} content={option.label} relationship="label" withArrow>
                <ToggleButton
                  appearance="subtle"
                  checked={option === active}
                  icon={<CellAlignmentIcon vertical={option.vertical} horizontal={option.horizontal} />}
                  onClick={() => setCellAlignment(editor, option.vertical, option.horizontal)}
                />
              </Tooltip>
            ))}
          </div>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default CellAlignmentMenuButton;
